/* eslint-disable no-await-in-loop, no-continue, no-restricted-syntax */
import { useEffect, useRef, useState } from 'react';
import { fetchEventSource, EventStreamContentType, type EventSourceMessage } from '@microsoft/fetch-event-source';
import { AccountInfo } from 'types/Account';
import { getLocalStorageString, setLocalStorageString } from 'utils/fnStorage';
import { randomIntBetween } from 'utils/fnData';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';

export interface SSEEvent {
    type: string;
    data: any;
    id?: string;
    resource?: string;
}

export interface SSEAccountData {
    account: AccountInfo;
    retrievedAt: number;
}

interface UseSSEParams {
    enabled: boolean;
    initialAccount: SSEAccountData | null;
    fetchAccount: () => Promise<SSEAccountData | null>;
    onEvent: (event: SSEEvent) => void;
}

type ConnectionOutcome = {
    type: 'closed' | 'error' | 'aborted';
    opened: boolean;
    openedAt: number;
    status: number;
};

const LAST_EVENT_ID_KEY = 'sse-last-event-id';
const LAST_EVENT_ID_HEADER = 'Last-Event-ID';

const RETRIES_PER_URL = 2;
const DEFAULT_RETRY_MIN_MS = 5_000;
const DEFAULT_RETRY_MAX_MS = 30_000;
const DEFAULT_BACKOFF_MS = 2 * 60 * 1_000;
const DEBOUNCE_MIN_MS = 1_000;
const DEBOUNCE_MAX_MS = 3_000;
const DEFAULT_SSE_TIMEOUT_S = 600;
const MIN_HEALTHY_UPTIME_MS = 2_000;
const NEAR_EXPIRY_MS = 5_000;

const EXPIRED = Symbol('sse-urls-expired');

const IS_DEV = process.env.NODE_ENV !== 'production';

const getBackoffMs = (): number => ApplicationConfig.api_config?.sse_backoff_ms ?? DEFAULT_BACKOFF_MS;

const getRetryMinMs = (): number => ApplicationConfig.api_config?.sse_retry_min_ms ?? DEFAULT_RETRY_MIN_MS;

const getRetryMaxMs = (): number => ApplicationConfig.api_config?.sse_retry_max_ms ?? DEFAULT_RETRY_MAX_MS;

const redactUrl = (url: string): string => url.replace(/(authorization=)[^&]+/gi, '$1<redacted>');

const sseLog = (...args: unknown[]) => {
    if (IS_DEV) console.log('[SSE]', ...args);
};

const sseError = (...args: unknown[]) => {
    if (IS_DEV) console.error('[SSE]', ...args);
};

export const useSSE = ({ enabled, initialAccount, fetchAccount, onEvent }: UseSSEParams) => {
    const [isConnected, setIsConnected] = useState(false);

    const mountedRef = useRef(true);
    const fetchAccountRef = useRef(fetchAccount);
    const onEventRef = useRef(onEvent);
    const initialAccountRef = useRef(initialAccount);
    const lastEventIdRef = useRef<string | null>(getLocalStorageString(LAST_EVENT_ID_KEY) || null);

    const bufferRef = useRef<Map<string, SSEEvent>>(new Map());
    const flushTimerRef = useRef<number | null>(null);

    fetchAccountRef.current = fetchAccount;
    onEventRef.current = onEvent;
    initialAccountRef.current = initialAccount;

    const setConnected = (value: boolean) => {
        if (mountedRef.current) setIsConnected(value);
    };

    const flushBuffer = () => {
        flushTimerRef.current = null;
        const events = Array.from(bufferRef.current.values());
        bufferRef.current.clear();
        events.forEach((event) => onEventRef.current(event));
    };

    const bufferEvent = (event: SSEEvent) => {
        const key = event.resource || event.type || 'default';
        bufferRef.current.set(key, event);
        if (flushTimerRef.current === null) {
            flushTimerRef.current = window.setTimeout(flushBuffer, randomIntBetween(DEBOUNCE_MIN_MS, DEBOUNCE_MAX_MS));
        }
    };

    const handleMessage = (type: 'message' | 'update', message: EventSourceMessage) => {
        if (!message.data) return;
        try {
            const data = JSON.parse(message.data);
            if (message.id) {
                lastEventIdRef.current = message.id;
                setLocalStorageString(LAST_EVENT_ID_KEY, message.id);
            }
            bufferEvent({ type, data, id: message.id, resource: data?.resource });
        } catch (error) {
            sseError(`error parsing ${type} data:`, error);
        }
    };

    const connectOnce = (url: string, signal: AbortSignal): Promise<ConnectionOutcome> =>
        new Promise((resolve) => {
            let opened = false;
            let openedAt = 0;
            let status = 0;
            let settled = false;

            const settle = (type: ConnectionOutcome['type']) => {
                if (settled) return;
                settled = true;
                resolve({ type, opened, openedAt, status });
            };

            fetchEventSource(url, {
                signal,
                openWhenHidden: true,
                headers: lastEventIdRef.current ? { [LAST_EVENT_ID_HEADER]: lastEventIdRef.current } : undefined,
                async onopen(response) {
                    status = response.status;
                    if (response.status === 401 || response.status === 403) {
                        throw new Error(`SSE auth failure ${response.status}`);
                    }
                    const contentType = response.headers.get('content-type') || '';
                    if (!response.ok || !contentType.includes(EventStreamContentType)) {
                        throw new Error(`SSE unexpected response ${response.status}`);
                    }
                    opened = true;
                    openedAt = Date.now();
                    setConnected(true);
                },
                onmessage(message) {
                    handleMessage(message.event === 'update' ? 'update' : 'message', message);
                },
                onclose() {
                    setConnected(false);
                },
                onerror(error) {
                    setConnected(false);
                    throw error;
                },
            })
                .then(() => settle(signal.aborted ? 'aborted' : 'closed'))
                .catch(() => settle(signal.aborted ? 'aborted' : 'error'));
        });

    const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
        new Promise((resolve) => {
            if (signal.aborted) {
                resolve();
                return;
            }
            let timer: number;
            const onAbort = () => {
                window.clearTimeout(timer);
                resolve();
            };
            timer = window.setTimeout(
                () => {
                    signal.removeEventListener('abort', onAbort);
                    resolve();
                },
                Math.max(0, ms)
            );
            signal.addEventListener('abort', onAbort, { once: true });
        });

    const run = async (signal: AbortSignal) => {
        let usedInitial = false;

        while (!signal.aborted) {
            try {
                sseLog('requesting new SSE URLs');

                let snapshot: SSEAccountData | null = null;
                if (!usedInitial) {
                    usedInitial = true;
                    const initial = initialAccountRef.current;
                    if (initial?.account?.sse_url) {
                        const timeoutMs = (initial.account.sse_timeout ?? DEFAULT_SSE_TIMEOUT_S) * 1_000;
                        if (Date.now() - initial.retrievedAt <= timeoutMs) {
                            snapshot = initial;
                        }
                    }
                }
                if (!snapshot) {
                    snapshot = await fetchAccountRef.current();
                }
                if (signal.aborted) return;

                if (!snapshot?.account?.sse_url) {
                    sseLog('no SSE URL available, backing off');
                    await sleep(getBackoffMs(), signal);
                    continue;
                }

                const { account, retrievedAt } = snapshot;
                const timeoutMs = (account.sse_timeout ?? DEFAULT_SSE_TIMEOUT_S) * 1_000;
                const urls = [account.sse_url, ...(account.fallback_sse_urls ?? [])].filter(Boolean);

                for (const url of urls) {
                    if (signal.aborted) return;

                    let retry = 0;
                    while (retry < RETRIES_PER_URL) {
                        if (Date.now() - retrievedAt > timeoutMs) throw EXPIRED;

                        sseLog(`connecting to ${redactUrl(url)} retry #${retry}`);
                        const outcome = await connectOnce(url, signal);
                        if (signal.aborted || outcome.type === 'aborted') return;

                        if (outcome.status === 401 || outcome.status === 403) {
                            sseLog(`auth failure ${outcome.status}, stopping`);
                            return;
                        }

                        if (Date.now() - retrievedAt > timeoutMs - NEAR_EXPIRY_MS) throw EXPIRED;

                        const uptime = outcome.opened ? Date.now() - outcome.openedAt : 0;
                        const wasHealthy = outcome.opened && uptime >= MIN_HEALTHY_UPTIME_MS;

                        if (outcome.type === 'closed' && wasHealthy) {
                            retry = 0;
                            continue;
                        }

                        const waitMs = randomIntBetween(getRetryMinMs(), getRetryMaxMs());
                        sseLog(`fail/disconnect, waiting for ${Math.round(waitMs / 1_000)} seconds`);
                        await sleep(waitMs, signal);
                        if (signal.aborted) return;

                        retry = wasHealthy ? 0 : retry + 1;
                    }
                }

                const exhaustedBackoffMs = getBackoffMs();
                sseLog(`all SSE URLs are exhausted, sleeping for ${Math.round(exhaustedBackoffMs / 1_000)} seconds`);
                await sleep(exhaustedBackoffMs, signal);
            } catch (error) {
                if (error === EXPIRED) {
                    sseLog('SSE URLs expired, restarting');
                    await sleep(randomIntBetween(getRetryMinMs(), getRetryMaxMs()), signal);
                    if (signal.aborted) return;
                    continue;
                }
                if (signal.aborted) return;
                sseError('loop error, backing off:', error);
                await sleep(getBackoffMs(), signal);
            }
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        if (!enabled) return undefined;

        const controller = new AbortController();
        run(controller.signal);

        return () => {
            controller.abort();
            if (flushTimerRef.current !== null) {
                window.clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
            }
            setConnected(false);
        };
    }, [enabled]);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    return { isConnected };
};
